import React, { useState, useEffect } from "react";
import { Card, Button, Typography, Space, Modal } from "antd";
import {
  AppstoreOutlined,
  InboxOutlined,
  SwapOutlined,
  DashboardOutlined,
  CalendarOutlined,
  LogoutOutlined,
  OneToOneOutlined,
  ShoppingCartOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { isNil } from "@salespark/toolkit";
import { useHistory } from "react-router-dom";
import { useAuth } from "services/Auth";
import OnlineComponents from "components/OnlineComponents";
import OffilineComponents from "components/OffilineComponents";
import { cropStaffName } from "utils/helper";
import { getActionsFromStore } from "utils/offlineCache";
import useSelectedService from "hooks/useSelectedService";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { confirm } = Modal;

const Dashboard = () => {
  const { setAuth } = useAuth();
  const history = useHistory();
  const { selectedService } = useSelectedService();
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    // Actions that are pure telemetry (not user data) must never count as pending sync items.
    const excludedSyncUrls = ["/info-capture", "/error-capture"];

    // Count individual pending items, not the number of queued HTTP actions.
    // Some actions (e.g. /goods/consumptions-sales) batch many movements into a single
    // queued action, so expand those into their real sub-item count.
    const countPendingItems = (actions) =>
      actions.reduce((total, action) => {
        if (excludedSyncUrls.includes(action?.url)) {
          return total;
        }
        const movements = action?.data?.movements;
        if (Array.isArray(movements) && movements.length > 0) {
          return total + movements.length;
        }
        return total + 1;
      }, 0);

    const checkPendingSync = async () => {
      const pendingActionsResponse = await getActionsFromStore();
      if (!isMounted) return;
      if (pendingActionsResponse.status === false) {
        setPendingSyncCount(0);
        return;
      }
      setPendingSyncCount(countPendingItems(pendingActionsResponse.data || []));
    };

    checkPendingSync();
    const intervalId = setInterval(checkPendingSync, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const btnStyle = {
    height: "auto",
    padding: "15px 24px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    backgroundColor: "#ffffff",
    border: "1px solid #F0F8FF",
    color: "#262626",
  };

  const btnStyleDisabled = {
    height: "auto",
    padding: "15px 24px",
    fontSize: 16,
    fontWeight: 400,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    backgroundColor: "#e2e2e2",
    border: "1px solid #b7b7b7",
    color: "#262626",
  };

  const getServiceState = () => {
    const hasService = !isNil(selectedService);

    const isServiceInitiated = hasService && !isNil(selectedService.start_date_effective);
    const isServiceEmbarked = hasService && !isNil(selectedService.embarked_count);
    const isServiceCompleted = hasService && !isNil(selectedService.end_date_effective);

    return {
      hasService,
      isServiceInitiated,
      isServiceCompleted,
      isServiceEmbarked,
      isServiceRunning: isServiceInitiated && !isServiceCompleted,
    };
  };

  const menuItems = [
    {
      title: "Serviço",
      icon: <AppstoreOutlined />,
      path: "/services",
      key: "service",
      getDisabled: (state) => false,
      getLabel: () => "",
    },
    {
      title: "Contagem de Arcas",
      icon: <InboxOutlined />,
      path: "/goods/stocks",
      key: "stock",
      getDisabled: (state) => !state.hasService, //!state.isServiceRunning || !state.isServiceEmbarked,
      getLabel: (state) => {
        if (!state.hasService) return "(Requer serviço)";
        // if (!state.isServiceInitiated) return "(Serviço não iniciado)";
        // if (!state.isServiceEmbarked) return "(Serviço sem embarques contados)";
        if (state.isServiceCompleted) return "(Serviço terminado)";
        return "";
      },
    },
    {
      title: "Transferência de Bebidas",
      icon: <SwapOutlined />,
      path: "/goods/transfers",
      key: "transfers",
      getDisabled: (state) => false,
      getLabel: () => "",
    },
    {
      title: "Consumos/Vendas",
      icon: <ShoppingCartOutlined />,
      path: "/goods/consumptions-sales",
      key: "consumptions-sales",
      className: "verde-agua",
      getDisabled: (state) => !state.isServiceRunning || !state.isServiceEmbarked,
      getLabel: (state) => {
        if (!state.hasService) return "(Requer serviço)";
        if (!state.isServiceInitiated) return "(Serviço não iniciado)";
        if (!state.isServiceEmbarked) return "(Serviço sem embarques contados)";
        if (state.isServiceCompleted) return "(Serviço terminado)";
        return "";
      },
    },
    {
      title: "Abastecimento de Combustível",
      icon: <DashboardOutlined />,
      path: "/goods/fuel",
      key: "fuel",
      getDisabled: (state) => false,
      getLabel: () => "",
    },
    {
      title: "Ocupação",
      icon: <OneToOneOutlined />,
      path: "/occupation",
      key: "occupation",
      getDisabled: (state) => false,
      getLabel: () => "",
    },
    {
      title: "Escala Semanal",
      icon: <CalendarOutlined />,
      path: "/mobile-schedule",
      key: "schedule",
      getDisabled: (state) => false,
      getLabel: () => "",
    },
  ];

  const logout = async () => {
    window.localStorage.removeItem("md-auth-boats");
    setAuth({ isSignedIn: false, data: null });
  };

  const switchUserOffline = async () => {
    window.localStorage.removeItem("md-auth-boats");
    setAuth({ isSignedIn: false, data: null });
    history.push("/auth/login");
  };

  const askToLogout = () => {
    confirm({
      title: "Confirma que pretende sair?",
      okText: "Sim",
      okType: "danger",
      cancelText: "Não",
      onOk() {
        logout();
      },
    });
  };

  const askToSwitchUserOffline = () => {
    confirm({
      title: "Confirma que pretende mudar de utilizador?",
      content: "Vais voltar ao ecrã de login para autenticar outro utilizador (modo offline).",
      okText: "Mudar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk() {
        switchUserOffline();
      },
    });
  };

  const serviceState = getServiceState();

  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%" }}>
      <Card
        size="small"
        style={{
          width: "60%",
          margin: "5px 12px",
          borderRadius: "12px",
          backgroundColor: "#f6ffed",
          border: "2px solid #52c41a",
          boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text style={{ fontSize: 16 }}>
              <strong>Serviço:</strong>{" "}
              {selectedService ? (
                <>
                  {selectedService?.product?.name} - {dayjs(selectedService.start_date).format("HH:mm")} <small>({selectedService.service_id_raw})</small>
                </>
              ) : (
                "Sem serviço selecionado"
              )}
            </Text>

            {selectedService?.equipment?.length > 0 && (
              <Text style={{ fontSize: 15 }}>
                <strong>Equipamento:</strong> {selectedService.equipment.map((e) => e.name).join(", ")}
              </Text>
            )}

            {selectedService?.staff?.length > 0 && (
              <Text style={{ fontSize: 15 }}>
                <strong>Staff:</strong> {selectedService.staff.map((e) => cropStaffName(e.name)).join(", ")}
              </Text>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {isNil(selectedService?.embarked_count) === false && (
              <Text style={{ fontSize: 15 }}>
                <strong>Total de Pessoas a Bordo:</strong> {selectedService.embarked_count}
              </Text>
            )}
            {selectedService?.start_date_effective && (
              <Text style={{ fontSize: 15 }}>
                <strong>Data de Início Real:</strong> {dayjs(selectedService.start_date_effective).format("HH:mm")}
              </Text>
            )}
            {selectedService?.end_date_effective && (
              <Text style={{ fontSize: 15 }}>
                <strong>Data de Fim Real:</strong> {dayjs(selectedService.end_date_effective).format("HH:mm")}
              </Text>
            )}
          </div>
        </div>
        <Space size={4}>
          {selectedService?.entries.map((entry) => {
            if (entry.product.product_for_service !== null) {
              return (
                <Text style={{ color: "#ff4d4f", fontWeight: 700 }}>
                  Desagua: {entry.product?.name} | {entry.passenger?.name ? entry.passenger.name : entry.entity?.name} | PAX:
                  {entry.passengersList?.map((p) => p.pax_i + p.pax_m + p.pax_z)}
                </Text>
              );
            } else {
              return null;
            }
          })}
        </Space>
      </Card>

      {pendingSyncCount > 0 && (
        <Card
          size="small"
          style={{
            width: "60%",
            margin: "5px 12px 20px 12px",
            borderRadius: "12px",
            backgroundColor: "#fffbe6",
            border: "2px solid #faad14",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <CloudUploadOutlined style={{ fontSize: 24, color: "#faad14" }} />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text style={{ fontSize: 16, fontWeight: 600 }}>
                {pendingSyncCount} {pendingSyncCount === 1 ? "item por sincronizar" : "itens por sincronizar"}
              </Text>
              <Text style={{ fontSize: 14, opacity: 0.75 }}>As ações serão sincronizadas automaticamente quando houver ligação à internet.</Text>
            </div>
          </div>
        </Card>
      )}

      <div style={{ minHeight: "100vh", width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Space orientation="vertical" size={10} style={{ width: "60%" }}>
            {menuItems.map((item) => {
              const isDisabled = item.getDisabled(serviceState);
              const extraLabel = item.getLabel(serviceState);

              return (
                <Button
                  key={item.key}
                  type="default"
                  icon={item.icon}
                  block
                  style={isDisabled ? btnStyleDisabled : btnStyle}
                  className={item.className}
                  disabled={isDisabled}
                  onClick={() => {
                    isNil(item.path) ? console.log("Path is null") : history.push(item.path);
                  }}
                >
                  <span style={{ marginLeft: 12 }}>
                    {item.title} {extraLabel && <span style={{ opacity: 0.7 }}>{extraLabel}</span>}
                  </span>
                </Button>
              );
            })}

            <OffilineComponents>
              <Button
                size="large"
                icon={<SwapOutlined />}
                block
                style={{
                  height: "auto",
                  padding: "20px 24px",
                  fontSize: 18,
                  fontWeight: 500,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  marginTop: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                onClick={askToSwitchUserOffline}
              >
                <span style={{ marginLeft: 12 }}>Mudar de Utilizador</span>
              </Button>
            </OffilineComponents>

            <OnlineComponents>
              <Button
                danger
                size="large"
                icon={<LogoutOutlined />}
                block
                style={{
                  height: "auto",
                  padding: "20px 24px",
                  fontSize: 18,
                  fontWeight: 500,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  marginTop: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                onClick={askToLogout}
              >
                <span style={{ marginLeft: 12 }}>SAIR</span>
              </Button>
            </OnlineComponents>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
